import { useQuery, useQueryClient } from '@tanstack/react-query';

import { GlobalAccount, GUID, PersonOrg, TaskStatus, TaskType } from '@ndvr/customer-api';

export enum TimePeriodUnit {
    DAY = 'day',
    WEEK = 'week',
    MONTH = 'month',
    YEAR = 'year',
}

export enum TaskLayout {
    LIST = 'list',
    KANBAN = 'kanban',
}

export enum TaskSortOption {
    DUE_DATE = 'dueDate',
    HOUSEHOLD = 'household',
    ASSIGNEE = 'assignee',
    TASK_TYPE = 'taskType',
}

export interface TaskViewState {
    timePeriodValue: number;
    timePeriodUnit: TimePeriodUnit;
    layout: TaskLayout;
    sortOption: TaskSortOption;
    sortDirection: 'asc' | 'desc';
    selectedAssignees: GUID<PersonOrg>[];
    selectedHouseholds: GUID<GlobalAccount>[];
    selectedTaskTypes: TaskType[];
    selectedTaskStatuses: TaskStatus[];
}

const TASK_VIEW_STATE_KEY = 'task-view-state';

const DEFAULT_TASK_VIEW_STATE: TaskViewState = {
    timePeriodValue: 30,
    timePeriodUnit: TimePeriodUnit.DAY,
    layout: TaskLayout.KANBAN,
    sortOption: TaskSortOption.DUE_DATE,
    sortDirection: 'asc',
    selectedAssignees: [],
    selectedHouseholds: [],
    selectedTaskTypes: [],
    selectedTaskStatuses: [],
};

const getLocalStorageTaskViewState = (): TaskViewState => {
    try {
        const stored = JSON.parse(localStorage.getItem(TASK_VIEW_STATE_KEY) ?? '{}');
        return {
            ...DEFAULT_TASK_VIEW_STATE,
            ...stored,
        };
    } catch {
        return DEFAULT_TASK_VIEW_STATE;
    }
};

const useTaskViewStateQuery = () => {
    return useQuery({
        queryKey: [TASK_VIEW_STATE_KEY],
        queryFn: getLocalStorageTaskViewState,
        gcTime: 0,
        staleTime: Infinity,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });
};

export const useTaskViewState = (): {
    taskViewState: TaskViewState;
    setTaskViewState: (partialState: Partial<TaskViewState>) => void;
} => {
    const queryClient = useQueryClient();
    const taskViewStateQuery = useTaskViewStateQuery();

    return {
        taskViewState: taskViewStateQuery.data ?? DEFAULT_TASK_VIEW_STATE,
        setTaskViewState: (partialState: Partial<TaskViewState>) => {
            localStorage.setItem(
                TASK_VIEW_STATE_KEY,
                JSON.stringify({ ...(taskViewStateQuery.data ?? DEFAULT_TASK_VIEW_STATE), ...partialState }),
            );
            queryClient.invalidateQueries({ queryKey: [TASK_VIEW_STATE_KEY] });
        },
    };
};

const TASK_OVERLAY_STATE_KEY = 'task-overlay-state';

export enum SheetType {
    VIEW = 'view',
}

export interface TaskOverlayState {
    taskGuid: string | null;
    sheetType: SheetType | null;
    deleteTaskGuid: string | null;
}

const DEFAULT_TASK_OVERLAY_STATE: TaskOverlayState = {
    taskGuid: null,
    sheetType: null,
    deleteTaskGuid: null,
};

const useTaskOverlayQuery = () =>
    useQuery<TaskOverlayState>({
        queryKey: [TASK_OVERLAY_STATE_KEY],
        queryFn: () => DEFAULT_TASK_OVERLAY_STATE,
        initialData: DEFAULT_TASK_OVERLAY_STATE,
        gcTime: 0,
        staleTime: Infinity,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });

export const useTaskOverlayState = () => {
    const queryClient = useQueryClient();
    const taskOverlayQuery = useTaskOverlayQuery();
    const setTaskOverlayState = (partialState: Partial<TaskOverlayState>) => {
        const current =
            queryClient.getQueryData<TaskOverlayState>([TASK_OVERLAY_STATE_KEY]) ?? DEFAULT_TASK_OVERLAY_STATE;
        queryClient.setQueryData([TASK_OVERLAY_STATE_KEY], { ...current, ...partialState });
    };

    return {
        taskOverlayState: taskOverlayQuery.data ?? DEFAULT_TASK_OVERLAY_STATE,
        setTaskOverlayState,
        closeAllOverlays: () => setTaskOverlayState(DEFAULT_TASK_OVERLAY_STATE),
        openViewTaskSheet: (taskGuid: string) => setTaskOverlayState({ taskGuid, sheetType: SheetType.VIEW }),
        openDeleteTaskDialog: (taskGuid: string) => setTaskOverlayState({ deleteTaskGuid: taskGuid }),
        closeDeleteTaskDialog: () => setTaskOverlayState({ deleteTaskGuid: null }),
    };
};
